/**
 * playIntegrityClient.ts
 *
 * Client-side Play Integrity token acquisition, shared by usePlayIntegrity
 * (post-auth verify via /api/v1/integrity/verify) and the pre-auth face
 * endpoints (face-login / face-recovery), where the client can't call the
 * verify route — it must send the raw token + requestHash so the endpoint
 * verifies inline.
 *
 * The requestHash binds the token to this action + package + timestamp so
 * the server can prove the token was minted for THIS request, not lifted
 * from another.
 */

import { PlayIntegrity } from '@/plugins/PlayIntegrity';
import { isCapacitor, isAndroid } from '@/lib/platformUtils';
import { getPortalRuntimeMetadata } from '@/lib/pwaAppIdentity';

// Cloud project number — public Google Cloud project identifier (not a secret).
// Set via NEXT_PUBLIC_PLAY_INTEGRITY_CLOUD_PROJECT_NUMBER env var at build time
// (the build script exports it), with hardcoded fallback.
export const PLAY_INTEGRITY_CLOUD_PROJECT_NUMBER =
  process.env.NEXT_PUBLIC_PLAY_INTEGRITY_CLOUD_PROJECT_NUMBER || '268799380699';

async function sha256(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

let providerPrepareInflight: Promise<boolean> | null = null;

/**
 * Warm the native token provider once. Safe to call repeatedly — the plugin
 * dedupes; we additionally serialize concurrent callers.
 */
export async function ensureIntegrityProviderReady(): Promise<boolean> {
  if (!isCapacitor() || !isAndroid() || !PLAY_INTEGRITY_CLOUD_PROJECT_NUMBER) {
    return false;
  }
  providerPrepareInflight ??= (async () => {
    try {
      const status = await PlayIntegrity.isTokenProviderReady();
      if (status.ready && !status.isStale) {
        return true;
      }
      const result = await PlayIntegrity.prepareTokenProvider({
        cloudProjectNumber: PLAY_INTEGRITY_CLOUD_PROJECT_NUMBER,
      });
      return Boolean(result.prepared || result.alreadyPreparing);
    } catch {
      return false;
    } finally {
      providerPrepareInflight = null;
    }
  })();
  return providerPrepareInflight;
}

export interface IntegrityTokenBundle {
  integrityToken: string;
  requestHash: string;
  packageName: string;
}

/**
 * Mint a Play Integrity token for a sensitive action. Returns null on any
 * failure — callers treat absence as neutral (web/iOS/older builds) and
 * only fail when the SERVER reports the token invalid.
 */
export async function requestIntegrityToken(
  action: string,
  requestData?: Record<string, unknown>,
): Promise<IntegrityTokenBundle | null> {
  if (!isCapacitor() || !isAndroid()) {
    return null;
  }

  try {
    const packageName = getPortalRuntimeMetadata().capacitorAppId;
    const payload = JSON.stringify({
      action,
      package: packageName,
      data: requestData ?? {},
      timestamp: Date.now(),
    });
    const requestHash = await sha256(payload);

    const ready = await ensureIntegrityProviderReady();
    if (!ready) {
      return null;
    }

    const tokenResult = await PlayIntegrity.requestToken({ requestHash });
    if (!tokenResult.success || !tokenResult.token) {
      return null;
    }

    return { integrityToken: tokenResult.token, requestHash, packageName };
  } catch {
    return null;
  }
}

/**
 * Mint a Play Integrity token bound to a specific approval decision —
 * the deterministic requestHash `ta_mfa_approve|<pkg>|<session>|<decision>`
 * is recomputed server-side (expectedApprovalRequestHash) so a token can
 * only ever prove THIS approval, on THIS challenge.
 */
export async function mintApprovalIntegrityToken(
  sessionId: string,
  decision: 'approved',
): Promise<IntegrityTokenBundle | null> {
  if (!isCapacitor() || !isAndroid()) {
    return null;
  }
  try {
    const packageName = getPortalRuntimeMetadata().capacitorAppId;
    const requestHash = await sha256(`ta_mfa_approve|${packageName}|${sessionId}|${decision}`);

    const ready = await ensureIntegrityProviderReady();
    if (!ready) {
      return null;
    }
    const tokenResult = await PlayIntegrity.requestToken({ requestHash });
    if (!tokenResult.success || !tokenResult.token) {
      return null;
    }
    return { integrityToken: tokenResult.token, requestHash, packageName };
  } catch {
    return null;
  }
}
